import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import type {
  ClinicianUpgradeCheckoutAction,
  ClinicianUpgradeCheckoutCurrentPlan,
  ClinicianUpgradeCheckoutSelection,
  ComplimentaryTrialEndAnalyticsContext,
  ComplimentaryTrialEndBannerAction,
  ComplimentaryTrialEndedModalAction,
  ComplimentaryTrialEndEntryPoint,
  ComplimentaryTrialEndModalAction,
} from '@heidi/commerce/contracts';
import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';

const TRACK_OPTIONS = { amplitude: false } as const;

const BANNER_ACTIONS = {
  keep_usage: ButtonAction.Heidi2TrialEndBannerKeepUsage,
  give_task: ButtonAction.Heidi2TrialEndBannerGiveTask,
  dismiss: ButtonAction.Heidi2TrialEndBannerDismiss,
} satisfies Record<ComplimentaryTrialEndBannerAction, ButtonAction>;

const MODAL_ACTIONS = {
  get_clinician_max: ButtonAction.Heidi2TrialEndModalGetClinicianMax,
  view_all_plans: ButtonAction.Heidi2TrialEndModalViewAllPlans,
  close: ButtonAction.Heidi2TrialEndModalClose,
} satisfies Record<ComplimentaryTrialEndModalAction, ButtonAction>;

const viewedProps = ({
  daysRemaining,
  usagePercent,
  cohort,
}: ComplimentaryTrialEndAnalyticsContext) => ({
  days_remaining: daysRemaining,
  usage_percent: usagePercent,
  is_low_usage: cohort === 'inactive',
});

const clickedProps = ({
  daysRemaining,
  usagePercent,
}: ComplimentaryTrialEndAnalyticsContext) => ({
  days_remaining: daysRemaining,
  usage_percent: usagePercent,
});

/** The strip banner is a nudge surface, so it reports as `modal_viewed` like the side-nav popover. */
export function trackComplimentaryTrialEndBannerViewed(
  context: ComplimentaryTrialEndAnalyticsContext
): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.Heidi2TrialEndBanner,
      ...viewedProps(context),
    },
  });
}

export function trackComplimentaryTrialEndBannerClick({
  action,
  label,
  ...context
}: ComplimentaryTrialEndAnalyticsContext & {
  action: ComplimentaryTrialEndBannerAction;
  label: string;
}): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Heidi2TrialEndBanner,
      action: BANNER_ACTIONS[action],
      label,
      ...clickedProps(context),
    },
  });
}

export function trackComplimentaryTrialEndModalViewed({
  entryPoint,
  ...context
}: ComplimentaryTrialEndAnalyticsContext & {
  entryPoint: ComplimentaryTrialEndEntryPoint;
}): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.Heidi2TrialEndModal,
      entry_point: entryPoint,
      ...viewedProps(context),
    },
  });
}

export function trackComplimentaryTrialEndModalClick({
  action,
  label,
  entryPoint,
  ...context
}: ComplimentaryTrialEndAnalyticsContext & {
  action: ComplimentaryTrialEndModalAction;
  label: string;
  entryPoint: ComplimentaryTrialEndEntryPoint;
}): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Heidi2TrialEndModal,
      action: MODAL_ACTIONS[action],
      label,
      entry_point: entryPoint,
      ...clickedProps(context),
    },
  });
}

const CHECKOUT_ACTIONS = {
  continue: ButtonAction.Heidi2TrialEndCheckoutContinue,
  cancel: ButtonAction.Heidi2TrialEndCheckoutCancel,
  seat_selected: ButtonAction.Heidi2TrialEndCheckoutSeatSelected,
  period_selected: ButtonAction.Heidi2TrialEndCheckoutPeriodSelected,
} satisfies Record<ClinicianUpgradeCheckoutAction, ButtonAction>;

const currentPlanProps = ({
  currentPlan,
  currentSeatType,
}: ClinicianUpgradeCheckoutCurrentPlan) => ({
  ...(currentPlan != null && { current_plan: currentPlan }),
  ...(currentSeatType != null && { current_seat_type: currentSeatType }),
});

export function trackClinicianUpgradeCheckoutViewed({
  currentPlan,
  daysRemaining,
}: ClinicianUpgradeCheckoutCurrentPlan & { daysRemaining: number | null }): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.Heidi2TrialEndCheckout,
      target_plan: SubscriptionTier.CLINICIAN_PRO,
      // `modal_viewed` has no seat field, so only the plan goes on the view.
      ...(currentPlan != null && { current_plan: currentPlan }),
      ...(daysRemaining != null && { days_remaining: daysRemaining }),
    },
  });
}

export function trackClinicianUpgradeCheckoutClick({
  action,
  label,
  seat,
  period,
  ...currentPlan
}: ClinicianUpgradeCheckoutSelection &
  ClinicianUpgradeCheckoutCurrentPlan & {
    action: ClinicianUpgradeCheckoutAction;
    label: string;
  }): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Heidi2TrialEndCheckout,
      action: CHECKOUT_ACTIONS[action],
      label,
      target_plan: SubscriptionTier.CLINICIAN_PRO,
      target_seat_type: seat === 'max' ? BillingSeatType.MAX : BillingSeatType.STANDARD,
      period,
      ...currentPlanProps(currentPlan),
    },
  });
}

const ENDED_MODAL_ACTIONS = {
  upgrade: ButtonAction.Heidi2TrialEndedModalUpgrade,
  stay: ButtonAction.Heidi2TrialEndedModalStay,
  close: ButtonAction.Heidi2TrialEndedModalClose,
} satisfies Record<ComplimentaryTrialEndedModalAction, ButtonAction>;

export type ComplimentaryTrialEndedModalAnalyticsContext = {
  currentPlan: SubscriptionTier | null;
  /** Share of the plan allowance used, capped at the schema's 100. */
  usagePercent: number | null;
};

/** Every variant sells a Clinician seat; clicks carry which one. */
export function trackComplimentaryTrialEndedModalViewed({
  currentPlan,
  usagePercent,
}: ComplimentaryTrialEndedModalAnalyticsContext): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.Heidi2TrialEndedModal,
      target_plan: SubscriptionTier.CLINICIAN_PRO,
      ...(currentPlan != null && { current_plan: currentPlan }),
      ...(usagePercent != null && { usage_percent: usagePercent }),
    },
  });
}

export function trackComplimentaryTrialEndedModalClick({
  action,
  label,
  targetSeatType,
  currentPlan,
  usagePercent,
}: ComplimentaryTrialEndedModalAnalyticsContext & {
  action: ComplimentaryTrialEndedModalAction;
  label: string;
  targetSeatType: BillingSeatType;
}): void {
  track({
    options: TRACK_OPTIONS,
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Heidi2TrialEndedModal,
      action: ENDED_MODAL_ACTIONS[action],
      label,
      target_plan: SubscriptionTier.CLINICIAN_PRO,
      target_seat_type: targetSeatType,
      ...(currentPlan != null && { current_plan: currentPlan }),
      ...(usagePercent != null && { usage_percent: usagePercent }),
    },
  });
}
